/**
 * Questions that are only asked when they apply.
 *
 * A form question can carry conditions -- "show this only when Country is
 * France" -- and they are the same `field op value` tree a view's filters are,
 * which is why the whole of the matching is `rowMatchesFilters` and not a
 * second implementation. What differs is what the tree is asked *about*: a view
 * asks it of a stored row, and a form asks it of what the visitor has typed so
 * far.
 *
 * Pure, so the whole of the visibility rule can be tested against plain drafts.
 */
import type { Field, ViewFilterGroup } from '@/api/types'
import { fieldKey } from '@/api/types'
import { VIEW_FILTER_TYPES, fieldIsCompatible } from '../filters'
import type { FilterContext, ViewFilterTypeDef } from '../filters'
import { rowMatchesFilters, type FilterableCells } from '../rows/rowMatch'
import { editorFor } from '../fields/ui/edit'
import type { Condition } from '../table/condition-row'

/** What a question stores about when to ask it. */
export interface ConditionalOptions {
  show_when_matching_conditions?: boolean
  condition_type?: string
  conditions?: readonly Condition[]
  condition_groups?: readonly ViewFilterGroup[]
}

/** The same, on a question the visitor is being shown. */
export interface ConditionalField extends ConditionalOptions {
  field: Field
}

/**
 * The questions a condition on this one may talk about: the ones asked before
 * it.
 *
 * Not a nicety. A question whose visibility depended on an answer further down
 * the form would be asking about something nobody has reached, and two such
 * questions could depend on each other. The backend does not enforce this --
 * it is the form's own rule, and it is enforced twice: the builder only offers
 * earlier questions, and the visitor's side is only ever *given* the earlier
 * ones, so a condition saved elsewhere that reaches forwards is ignored rather
 * than obeyed.
 */
export function conditionSources(asked: readonly Field[], field: Field): Field[] {
  const index = asked.findIndex((candidate) => candidate.id === field.id)
  return index === -1 ? [] : asked.slice(0, index)
}

/**
 * The conditions still worth asking, which is one rule and not two.
 *
 * A comparison that no longer suits the field it names is dropped: a field
 * turned from a date into text keeps whatever date condition was written about
 * it, and asking a date comparison of a string is asking nonsense -- so the
 * question is shown rather than hidden by a rule nobody can read any more.
 *
 * The *other* restriction -- that a condition may only be about a question
 * asked earlier -- is deliberately not enforced here. The tree is handed only
 * the earlier fields, and it already skips a filter naming a field it was not
 * given, so a condition reaching forwards is passed through to be ignored
 * there. Dropping it here as well would be the same rule in two places, which
 * is one place for them to stop agreeing.
 */
function readableConditions(
  entry: ConditionalOptions,
  before: readonly Field[],
): Condition[] {
  return (entry.conditions ?? []).filter((condition) => {
    const on = before.find((candidate) => candidate.id === condition.field)
    if (on === undefined) return true
    const definition = VIEW_FILTER_TYPES[
      condition.type as keyof typeof VIEW_FILTER_TYPES
    ] as ViewFilterTypeDef | undefined
    return definition !== undefined && fieldIsCompatible(definition, on)
  })
}

/**
 * What the conditions are asked about: the answers so far, with every question
 * that is not on screen contributing what an empty box would send.
 *
 * That is the rule worth stating. A question hidden by an earlier condition
 * must not go on deciding a later one with the answer it held before it
 * disappeared -- otherwise turning one answer back and forth leaves the form in
 * a state nobody typed. `fromDraft(field, '')` is the field type's own answer
 * to "what does an empty box send", so an unticked checkbox is `false` and an
 * empty link row is a list of nothing, rather than all of them being `null`.
 */
function answersSoFar(
  entries: readonly ConditionalField[],
  shown: ReadonlySet<number>,
  drafts: Record<number, string>,
): FilterableCells {
  const row: FilterableCells = {}
  for (const { field } of entries) {
    const editor = editorFor(field)
    if (editor === null) continue
    const draft = shown.has(field.id) ? (drafts[field.id] ?? '') : ''
    row[fieldKey(field.id)] = editor.fromDraft(field, draft)
  }
  return row
}

/**
 * The questions to ask, in order, given what has been answered.
 *
 * Decided in order and cumulatively, because a question can only be conditioned
 * on the ones before it: by the time each is reached, every answer it could be
 * about has already been settled as visible or not.
 */
export function visibleFormFields<T extends ConditionalField>(
  entries: readonly T[],
  drafts: Record<number, string>,
  context?: FilterContext,
): T[] {
  const visible: T[] = []
  const shown = new Set<number>()

  entries.forEach((entry, index) => {
    const keep = (): void => {
      visible.push(entry)
      shown.add(entry.field.id)
    }

    if (entry.show_when_matching_conditions !== true) {
      keep()
      return
    }

    const before = entries.slice(0, index).map(({ field }) => field)
    const matches = rowMatchesFilters(
      answersSoFar(entries, shown, drafts),
      {
        // The backend's own default, stated rather than left to a coincidence.
        filter_type: entry.condition_type ?? 'AND',
        filters: readableConditions(entry, before),
        filter_groups: entry.condition_groups ?? [],
      },
      before,
      context,
    )

    // No separate "and if there are no conditions, show it" rule: an empty list
    // of filters already keeps every row, and saying it twice would be two
    // places for it to stop agreeing.
    if (matches) keep()
  })

  return visible
}

/**
 * A question's conditions, split into the ones this build can edit and the
 * ones it cannot.
 *
 * Groups are not built here, for the reason the filter menu does not build
 * them: flattening a nested condition into the list would show the form doing
 * something it is not. They still have to be *carried*, which is not true of
 * the filter menu -- see `conditionsAfter`.
 */
export function splitConditions(entry: ConditionalOptions): {
  rows: Condition[]
  grouped: Condition[]
} {
  const all = entry.conditions ?? []
  return {
    rows: all.filter(
      (condition) => condition.group === null || condition.group === undefined,
    ),
    grouped: all.filter(
      (condition) => condition.group !== null && condition.group !== undefined,
    ),
  }
}

/**
 * The whole list to send after editing the ungrouped ones.
 *
 * **The endpoint replaces rather than merges**: it deletes every condition of
 * the field options it is given that the payload does not name. So the ones
 * inside groups have to be sent back untouched, or editing a condition this
 * build understands would silently destroy the ones it does not.
 */
export function conditionsAfter(
  entry: ConditionalOptions,
  rows: readonly Condition[],
): Condition[] {
  return [...rows, ...splitConditions(entry).grouped]
}

/** The id a condition that does not exist yet is sent with. */
export const NEW_CONDITION = 0

/**
 * A condition about the last question before this one, with the first
 * comparison that question accepts.
 *
 * The last rather than the first, because it is the question nearest the one
 * being conditioned -- and on a long form the one whose answer is most likely
 * to be what this depends on.
 */
export function newCondition(before: readonly Field[]): Condition | null {
  const field = before.at(-1)
  if (field === undefined) return null
  const definition = Object.values(VIEW_FILTER_TYPES).find((candidate) =>
    fieldIsCompatible(candidate as ViewFilterTypeDef, field),
  ) as ViewFilterTypeDef | undefined
  if (definition === undefined) return null
  return { id: NEW_CONDITION, field: field.id, type: definition.type, value: '' }
}
